import { useQuery } from '@tanstack/react-query';
import { useOrgId } from '@/lib/pulse/useEmployee';
import { myPulses, type PulseRow } from '@/lib/pulse/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PulsesTable } from '@/components/employee/PulsesTable';
import { ConversationsList } from '@/components/employee/ConversationsList';
import { MyTimelineChart } from '@/components/employee/MyTimelineChart';
import { YouVsOrgChart } from '@/components/employee/YouVsOrgChart';

export default function MyData() {
  const orgId = useOrgId();

  const { data: pulses = [] } = useQuery({
    queryKey: ['my_pulses', orgId, 90],
    queryFn: async () => {
      const { data, error } = await myPulses(orgId!, 90);
      if (error) throw error;
      return (data ?? []) as PulseRow[];
    },
    enabled: !!orgId,
    staleTime: 0,
  });

  if (!orgId) return null;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8 space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold">Your Data</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Everything you've shared. Erase anything, any time — it's permanent.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">How you compare</CardTitle>
          <CardDescription>
            Only you can see this. Team line hides any day with fewer than 5 check-ins.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <YouVsOrgChart orgId={orgId} myPulses={pulses} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">History</CardTitle>
          <CardDescription>Only visible to you.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="timeline">
            <TabsList>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="pulses">Pulses</TabsTrigger>
              <TabsTrigger value="conversations">Conversations</TabsTrigger>
            </TabsList>
            <TabsContent value="timeline" className="pt-4">
              <MyTimelineChart orgId={orgId} />
            </TabsContent>
            <TabsContent value="pulses" className="pt-4">
              <PulsesTable orgId={orgId} />
            </TabsContent>
            <TabsContent value="conversations" className="pt-4">
              <ConversationsList orgId={orgId} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
